import { FUSO_EXIBICAO } from 'src/app/shared/tabela';
import { baixarBlob } from '../../../../helpers/download.helpers';
import type { CheckinRowVm } from './checkin-row.vm';

/**
 * Exportação de check-ins em CSV gerado no cliente.
 *
 * Não existe endpoint de exportação de ingressos: `VENDAS_EXPORTAR` é de
 * VENDAS e manda o relatório por e-mail. Como a tela já tem as linhas
 * formatadas (`CheckinRowVm`), o CSV sai daqui — funções puras, sem Angular,
 * sem HTTP.
 *
 * Só o `baixarCsv` toca o DOM; `linhasParaCsv` e `nomeArquivoCsv` são puras e
 * é nelas que mora o teste.
 */

/**
 * Consentimento LGPD do diálogo de exportação.
 *
 * Texto idêntico ao de `exportarVendas` (vendas-eventos.component.ts). Mora
 * aqui como constante porque a fronteira do módulo (`boundary.spec.ts`) proíbe
 * `eventos/checkin/` importar de `eventos/sub-pages/` — e porque o texto é
 * conteúdo, não código: reescrevê-lo em cada tela é como o aviso jurídico
 * diverge sem ninguém perceber.
 *
 * HTML pronto para o `html:` do SweetAlert.
 */
export const TEXTO_CONSENTIMENTO_EXPORTACAO = `
        <p style="text-align: left;">
          Ao acessar esta lista, você poderá visualizar dados pessoais dos participantes. Como produtor, é sua responsabilidade garantir o uso adequado dessas informações, evitando qualquer prática que possa comprometer a privacidade dos usuários, como o compartilhamento ou a comercialização de contatos.
        </p>
        <p style="text-align: left; margin-top: 1.5rem;">
          Ao prosseguir, você confirma estar ciente dessas responsabilidades.
        </p>
      `;

/**
 * Colunas do CSV. Mesma ordem e mesmos rótulos das colunas da tabela, mais
 * `Observações` — que não cabe na grade mas é o campo que o produtor mais
 * procura depois do evento.
 */
/**
 * Parâmetros de UMA página da exportação.
 *
 * Existe separado porque o erro aqui é silencioso: a exportação mandava só o
 * termo de busca e ignorava sessão, ordenação e os filtros dinâmicos. Como o
 * número de páginas sai de `totalRows()`, que É filtrado, o arquivo vinha com
 * um prefixo NÃO filtrado do mesmo tamanho — parecia certo e estava errado.
 *
 * A separação em dois objetos não é capricho: o client gerado só declara os
 * params nomeados (ADR-0019 — o contrato não nomeia filtro nenhum), então as
 * chaves `campo[operador]` do catálogo dinâmico têm de ir por
 * `options.params`. É a mesma divisão que o `fetcher` da lista faz.
 */
export function paramsExportacao(entrada: {
  page: number;
  size: number;
  termo: string;
  ordem: string;
  sessaoId: string | null;
  filtrosDinamicos: Readonly<Record<string, string>>;
}): {
  tipados: Record<string, unknown>;
  dinamicos: Record<string, string>;
} {
  const termo = entrada.termo.trim();
  return {
    tipados: {
      page: entrada.page,
      size: entrada.size,
      sort: entrada.ordem,
      sessaoId: entrada.sessaoId,
      ...(termo ? { filtro: termo } : {}),
    },
    dinamicos: { ...entrada.filtrosDinamicos },
  };
}

const COLUNAS: readonly { header: string; valor: (l: CheckinRowVm) => string }[] = [
  { header: 'Status', valor: (l) => l.statusLabel },
  { header: 'Nome', valor: (l) => l.nome },
  { header: 'E-mail', valor: (l) => l.email },
  { header: 'Código', valor: (l) => l.codigo },
  { header: 'Documento', valor: (l) => l.documento },
  { header: 'Lote', valor: (l) => l.lote },
  { header: 'Janela', valor: (l) => l.janelaResumo },
  { header: 'Check-in', valor: (l) => l.dataCheckinLabel },
  { header: 'Operador', valor: (l) => l.operador },
  { header: 'Observações', valor: (l) => l.observacoes },
];

/**
 * BOM UTF-8. Sem ele o Excel pt-BR abre o arquivo em Windows-1252 e "Início"
 * vira "InÃ­cio" — o acento é a regra, não a exceção, nos nomes daqui.
 */
const BOM = '\uFEFF';

/** Excel pt-BR usa `;` como separador de lista; `,` cai tudo numa coluna só. */
const SEPARADOR = ';';

/** CRLF: o que o Excel espera, e o que o Sheets também aceita. */
const QUEBRA = '\r\n';

/**
 * Todo campo sai entre aspas — assim `;`, `"`, quebra de linha e `=` inicial
 * não têm caminho de fuga. Aspas internas são escapadas por duplicação (RFC 4180).
 */
function campo(valor: string): string {
  return `"${valor.replace(/"/g, '""')}"`;
}

/** CSV completo (com BOM). Lista vazia devolve só o cabeçalho. */
export function linhasParaCsv(linhas: CheckinRowVm[]): string {
  const cabecalho = COLUNAS.map((c) => campo(c.header)).join(SEPARADOR);
  const corpo = linhas.map((l) => COLUNAS.map((c) => campo(c.valor(l))).join(SEPARADOR));
  return BOM + [cabecalho, ...corpo].join(QUEBRA);
}

/** `YYYY-MM-DD` no fuso do evento — não no fuso de quem clicou. */
const DATA_ARQUIVO = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSO_EXIBICAO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Slug ASCII: sem acento, sem espaço, sem pontuação. Nome de arquivo com
 * acento ainda quebra em anexo de e-mail e em servidor de arquivos alheio.
 */
export function slugArquivo(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

/** `checkins-<slug-do-evento>-<YYYY-MM-DD>.csv`. */
export function nomeArquivoCsv(nomeEvento: string, agora: Date): string {
  const slug = slugArquivo(nomeEvento ?? '');
  const data = DATA_ARQUIVO.format(agora);
  return `checkins-${slug || 'evento'}-${data}.csv`;
}

/** Monta o Blob do CSV; quem toca o DOM é o `baixarBlob` compartilhado. */
export function baixarCsv(conteudo: string, nomeArquivo: string): void {
  baixarBlob(new Blob([conteudo], { type: 'text/csv;charset=utf-8;' }), nomeArquivo);
}
